import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { CustomerShell } from "@/components/CustomerShell";
import { StatusBadge } from "@/components/StatusBadge";
import { serviceIcon, SERVICE_ACCENT } from "@/lib/constants";
import { CalendarCheck, ChevronRight, Loader2, Plus } from "lucide-react";

export default function Bookings() {
    const [rows, setRows] = useState(null);
    const [masters, setMasters] = useState([]);
    const { t } = useLang();
    const nav = useNavigate();

    useEffect(() => {
        api.get("/bookings").then((r) => setRows(r.data.filter((b) => !b.master_booking_id))).catch(() => setRows([]));
        const last = (m) => (m.children || []).reduce((a, c) => ((c.updated_at || c.created_at || "") > a ? (c.updated_at || c.created_at) : a), m.created_at || "");
        api.get("/master-bookings").then((r) => setMasters([...r.data].sort((a, b) => last(b).localeCompare(last(a))))).catch(() => {});
    }, []);

    return (
        <CustomerShell>
            <div className="bg-navy px-5 pb-5 pt-6 text-white rounded-b-3xl">
                <h1 className="font-heading text-2xl font-extrabold">{t("My Bookings")}</h1>
                <p className="text-sm text-white/60">{t("Track all your home service requests")}</p>
            </div>
            <div className="px-5 py-5">
                {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                {rows && rows.length === 0 && masters.length === 0 && (
                    <div className="mt-10 flex flex-col items-center text-center">
                        <div className="rounded-full bg-navy/5 p-5"><CalendarCheck size={40} className="text-navy/30" /></div>
                        <h3 className="mt-4 font-heading text-lg font-bold text-navy">{t("No bookings yet")}</h3>
                        <p className="mt-1 text-sm text-navy/50">{t("Book your first home service in under a minute.")}</p>
                        <button data-testid="empty-book-btn" onClick={() => nav("/book")} className="mt-5 flex items-center gap-2 rounded-full bg-orange px-6 py-3 text-sm font-bold text-white transition-transform active:scale-95">
                            <Plus size={18} /> {t("Book a Service")}
                        </button>
                    </div>
                )}
                <div className="space-y-3">
                    {masters.map((m) => (
                        <button key={m.id} data-testid={`master-row-${m.booking_id}`} onClick={() => nav(`/bookings/master/${m.id}`)} className="flex w-full items-center gap-3 rounded-2xl border border-orange/30 bg-white p-4 text-left transition-transform active:scale-[0.98]">
                            <div className="flex-1">
                                <div className="flex items-center gap-2"><span className="font-heading text-sm font-extrabold text-navy">{m.booking_id}</span><span className="rounded-full bg-orange/15 px-2 py-0.5 text-[10px] font-bold text-orange-dark">{m.children.length} {t("services")}</span></div>
                                <p className="mt-0.5 text-sm font-medium text-navy/80">{m.children.map((c) => c.service_name.replace(" Visit", "")).join(" · ")}</p>
                                <p className="text-xs text-navy/45">{m.preferred_date} · {m.preferred_window} · {m.summary}</p>
                            </div>
                            <ChevronRight size={20} className="text-navy/30" />
                        </button>
                    ))}
                    {rows?.map((b) => {
                        const accent = SERVICE_ACCENT[b.service_name?.toLowerCase().includes("elec") ? "electrician" : b.service_name?.toLowerCase().includes("plumb") ? "plumber" : "ac"];
                        return (
                            <button
                                key={b.id}
                                data-testid={`booking-row-${b.booking_id}`}
                                onClick={() => nav(`/bookings/${b.id}`)}
                                className="flex w-full items-center gap-3 rounded-2xl border border-navy/10 bg-white p-4 text-left transition-transform active:scale-[0.98]"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="font-heading text-sm font-extrabold text-navy">{b.booking_id}</span>
                                        {b.estimate?.status === "PENDING" && <span className="rounded-full bg-orange/15 px-2 py-0.5 text-[10px] font-bold text-orange-dark">{t("Approval needed")}</span>}
                                    </div>
                                    <p className="mt-0.5 text-sm font-medium text-navy/80">{b.service_name} · {b.problem}</p>
                                    <p className="text-xs text-navy/45">{b.preferred_date} · {b.preferred_window}</p>
                                    <div className="mt-2"><StatusBadge status={b.status} /></div>
                                </div>
                                <ChevronRight size={20} className="text-navy/30" />
                            </button>
                        );
                    })}
                </div>
            </div>
        </CustomerShell>
    );
}
